import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import supabase, { isSupabaseAuthEnabled } from '../services/supabase';
import { useBranding } from '../context/BrandingContext';

const Verify2FA = () => {
  const { branding } = useBranding();
  // Match Snapcom brand blue used across the marketing pages and email
  // primary color when the tenant has no override — was Tailwind
  // blue-500 (#3B82F6), which read as "off" next to the deeper brand
  // blue everywhere else in the auth flow.
  const primary = branding.primary_color || '#0066CC';
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { userId, email, password } = location.state || {};

  // If no userId in state, redirect to login
  React.useEffect(() => {
    if (!userId) {
      navigate('/login');
    }
  }, [userId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.verify2FA(userId, code, trustDevice);

      // If "trust this device" was checked, the backend minted a
      // one-time token. Store it keyed by email so a shared browser
      // can hold tokens for multiple users without cross-bleed.
      if (response.data?.trustToken && email) {
        const emailKey = email.toLowerCase().trim();
        localStorage.setItem(`trustedDeviceToken:${emailKey}`, response.data.trustToken);
      }

      // Supabase auth path: code is verified, but we signed out earlier
      // because the partial session shouldn't be usable until 2FA passed.
      // Re-authenticate with Supabase using the password held in router
      // state. AuthContext's auth state listener picks up the new session
      // and stores tokens/user.
      if (isSupabaseAuthEnabled() && password && email) {
        // Fresh login: wipe any stale masquerade state before establishing
        // the new session, otherwise initSupabaseAuth takes the masquerade
        // early-return and ignores the Supabase session we're about to create.
        localStorage.removeItem('isMasquerading');
        localStorage.removeItem('originalAccessToken');
        localStorage.removeItem('originalRefreshToken');
        localStorage.removeItem('originalUser');
        localStorage.removeItem('originalSupabaseAuth');
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) {
          setError(signInError.message || 'Failed to complete sign-in after 2FA');
          setLoading(false);
          return;
        }
        window.location.href = '/';
        return;
      }

      // Legacy auth path: backend returned tokens; persist them.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('isMasquerading');
      localStorage.removeItem('originalAccessToken');
      localStorage.removeItem('originalRefreshToken');
      localStorage.removeItem('originalUser');
      localStorage.removeItem('originalSupabaseAuth');

      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('Resend feature not yet implemented. Please try logging in again.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Logo + app name in the same brand-pill treatment used on the
              Login page, so a tenant that comes here from /login sees a
              consistent identity strip instead of a naked logo. */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-900 text-white">
              {branding.logo_url && (
                <img src={branding.logo_url} alt="" className="h-6 w-auto" />
              )}
              <span className="text-sm font-semibold tracking-tight">{branding.app_name || 'SnapText'}</span>
            </div>
          </div>
          <h3 className="mt-2 text-center text-2xl font-bold text-gray-900">
            Two-Factor Authentication
          </h3>
          <p className="mt-2 text-center text-sm text-gray-600">
            We sent a 6-digit code to <strong>{email}</strong>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              id="code"
              name="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="6"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ '--tw-ring-color': primary }}
              className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:border-transparent text-center text-2xl tracking-widest font-bold"
              placeholder="000000"
              autoFocus
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              Code expires in 10 minutes
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              className="rounded border-gray-300 focus:ring-2"
              style={{ accentColor: primary }}
            />
            <span>Trust this device for 7 days</span>
          </label>

          <div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              style={{ backgroundColor: primary }}
              className="group relative w-full flex justify-center py-2.5 px-4 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleResend}
              style={{ color: primary }}
              className="text-sm font-semibold hover:underline"
            >
              Didn't receive a code? Resend
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-gray-600 hover:text-gray-500"
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Verify2FA;
